import * as React from 'react';

import classNames from 'classnames';

import {Pencil} from 'shells/icon';

import {Button} from '../button/button';
import {SaveButton} from '../button/save-button/save-button';
import type {SectionName} from '../sales-documents/helpers';
import {Body} from '../typography/body';
import {SectionLabel} from '../typography/section-label';

import styles from './saveable-wrapper.module.css';

type Props = {
    children: React.ReactNode;
    sectionName: SectionName;
    onSave: () => void;
    onCancel: () => void;
    isActive: boolean;
    onToggleActive: (arg1: SectionName) => void;
    noPadding?: boolean;
    overflowVisible?: boolean;
    isDisabled?: boolean;
    errorMessage?: string | null | undefined;
    // Some quote states are immutable and cannot be edited
    isImmutable?: boolean;
    // Some section always want to show a border, even when immutable
    showBorderWhenImmutable?: boolean;
    showEditIcon?: boolean;
    centerContent?: boolean;
};

export function SaveableWrapper(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(
        props.errorMessage || null
    );
    const [isHovering, setIsHovering] = React.useState<boolean>(false);

    const containerRef = React.useRef<HTMLDivElement | null | undefined>(null);

    React.useEffect(() => {
        setErrorMessage(props.errorMessage || null);
    }, [props.errorMessage]);

    const handleSave = () => {
        setIsSaving(true);
        try {
            props.onSave();
        } catch (error: any) {
            setErrorMessage(error.message);
        } finally {
            setIsSaving(false);
        }
        if (!props.errorMessage) {
            setErrorMessage(null);
        }
    };

    const handleCancel = () => {
        props.onCancel();
        setErrorMessage(null);
    };

    const handleClick = (e: Event) => {
        // Don't allow immutable sections to become active
        if (!props.isActive && props.isImmutable) {
            return;
        }
        e.stopPropagation();
        if (!props.isActive && !props.isDisabled) {
            props.onToggleActive(props.sectionName);
        }
    };

    const shouldShowBorder = !props.isImmutable || props.showBorderWhenImmutable;

    const baseClasses = 'br-3 full-height border-box';
    const className = classNames(baseClasses, {
        'active-border': props.isActive,
        'pad-16': !props.noPadding,
        'overflow-visible': props.overflowVisible,
        'overflow-hidden': !props.overflowVisible,
        'hover-border': isHovering && !props.isActive && !props.isDisabled && !props.isImmutable,
        'inactive-border': (!props.isActive && shouldShowBorder) || props.isDisabled,
        'width-fit-content': props.centerContent,
        'full-width': !props.centerContent,
    });

    return (
        <div
            // @ts-expect-error
            onClick={handleClick}
            className={classNames('flex', 'flex-dir-col', 'full-width', {
                ['full-height']: props.sectionName,
                ['align-center']: props.centerContent,
            })}
            // @ts-expect-error
            ref={containerRef}
        >
            <div className={styles.header}>
                <div className='flex gap-8'>
                    <SectionLabel noBottomBorder={true} noBottomMargin={true}>
                        {props.sectionName}
                    </SectionLabel>
                    {props.showEditIcon && (
                        <div data-tip='Edit' className={classNames('mb-8', styles['edit-icon'])}>
                            <Pencil />
                        </div>
                    )}
                </div>

                <div className='flex gap-12 mb-8'>
                    {props.isActive && (
                        <>
                            <Button variant='text' onClick={handleCancel}>
                                Cancel
                            </Button>
                            {/* @ts-expect-error */}
                            <SaveButton onClick={handleSave} isSaving={isSaving} size='normal' />
                        </>
                    )}
                </div>
            </div>
            <div
                className={className}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <div
                    className={classNames(styles['component-children'], {
                        [styles['fit-content']]: props.centerContent,
                    })}
                >
                    {props.children}
                </div>
            </div>
            {errorMessage && (
                <Body color='error' size='small' isItalic={true}>
                    {errorMessage}
                </Body>
            )}
        </div>
    );
}
